import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getRegionConfig } from '../config/regionConfig';

const defaultLanguageContext = {
  language: 'en',
  direction: 'ltr',
  isRTL: false,
  changeLanguage: () => {},
  availableLanguages: [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'ur', name: 'اردو', flag: '🇵🇰' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ]
};

const LanguageContext = createContext(defaultLanguageContext);

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const useLanguage = () => {
  return useContext(LanguageContext);
};

export const LanguageProvider = ({ children, baseTheme }) => {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState('ltr');

  // On first mount: if region config specifies a default language AND no
  // explicit user preference is stored in localStorage, apply it.
  useEffect(() => {
    const stored = localStorage.getItem('i18nextLng');
    const regionCfg = getRegionConfig();
    const regionDefault = regionCfg?.defaultLanguage;

    if (regionDefault && !stored) {
      i18n.changeLanguage(regionDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  const isUrdu = i18n.language === 'ur';

  useEffect(() => {
    const newDirection = isRTL ? 'rtl' : 'ltr';
    setDirection(newDirection);
    document.dir = newDirection;
    document.documentElement.setAttribute('dir', newDirection);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language, isRTL]);

  const rtlFontFamily = isUrdu
    ? '"Noto Nastaliq Urdu", "Noto Sans Arabic", "Arial", sans-serif'
    : '"Noto Sans Arabic", "Arial", sans-serif';

  const theme = React.useMemo(() => createTheme(baseTheme, {
    direction,
    typography: {
      fontFamily: isRTL ? rtlFontFamily : baseTheme.typography.fontFamily,
      ...(isRTL && isUrdu && {
        body1: { lineHeight: 1.8 },
        body2: { lineHeight: 1.8 },
        button: { lineHeight: 1.8 },
      }),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            direction,
            fontFamily: isRTL ? rtlFontFamily : baseTheme.typography.fontFamily,
            ...(isRTL && isUrdu && { lineHeight: 1.8 }),
          }
        }
      },
      ...(isRTL && {
        MuiInputLabel: {
          styleOverrides: {
            root: {
              transformOrigin: 'top right',
              right: 28,
              left: 'auto',
            },
            shrink: {
              transformOrigin: 'top right',
              right: 28,
              left: 'auto',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              textAlign: 'right',
              '& legend': {
                marginRight: 8,
                marginLeft: 0,
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: {
              paddingRight: '14px !important',
              paddingLeft: '32px !important',
            },
            icon: {
              right: 'auto',
              left: 7,
            },
          },
        },
      }),
    }
  }), [baseTheme, direction, isRTL, isUrdu, rtlFontFamily]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const value = {
    language: i18n.language,
    direction,
    isRTL,
    changeLanguage,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'ar', name: 'العربية', flag: '🇸🇦' },
      { code: 'ur', name: 'اردو', flag: '🇵🇰' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'zh', name: '中文', flag: '🇨🇳' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </LanguageContext.Provider>
  );
};
